import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import EventTimeline from "../../components/Shared/EventTimeline";
import Job from "../../models/Job";
import { useJobHistory } from "../../networking/job.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { getServiceName } from "../../utils/competence.utils";
import {
	JOB_DOT_CLASS_BY_SOURCE,
	JOB_DOT_CLASS_BY_TYPE,
	JOB_TIMELINE_PREFIX,
	jobLabelValues,
	jobSubjectContactId,
	renderJobExtraRows,
	renderJobMeta,
} from "./jobTimeline.config";

interface Props {
	job: Job;
}

/**
 * "Händelser" tab — the unified event timeline for a job. Fetches on tab
 * activation (RouteTabs mounts only the active tab's content).
 *
 * The job flavour of the shared EventTimeline: everything job-specific (dot
 * colours, label interpolation, shift and denial-reason rows) lives in
 * jobTimeline.config and is passed in here.
 */
const JobHistoryTab: React.FC<Props> = ({ job }) => {
	const { t } = useTranslation();
	const { entries, isLoading, isError } = useJobHistory({ jobId: job.id });
	const { data: organisation } = useOrganisation();

	// Requirement diff rows reference the org taxonomies by key — resolve to
	// display names (keys render as-is when a type has since been removed).
	const resolveRequirementName = useCallback(
		(kind: "certification" | "tag", key: string) => {
			const types =
				kind === "certification"
					? organisation?.certificationTypes
					: organisation?.consultantTagTypes;

			return types?.find((type) => type.key === key)?.name?.sv;
		},
		[organisation]
	);

	// Competence change rows store {branch, service} keys — resolve to the org
	// competence tree's service name.
	const resolveCompetenceName = useCallback(
		(branch: string, service: string) => {
			const name = getServiceName(organisation, branch, service);

			return typeof name === "string" ? name : undefined;
		},
		[organisation]
	);

	if (isError)
		return (
			<div className="py-8 text-center text-sm text-red-600">{t("eventTimeline.loadError")}</div>
		);

	if (isLoading || !entries) return null;

	return (
		<div className="max-w-2xl py-4">
			<EventTimeline
				entries={entries}
				i18nPrefix={JOB_TIMELINE_PREFIX}
				dotClassByType={JOB_DOT_CLASS_BY_TYPE}
				dotClassBySource={JOB_DOT_CLASS_BY_SOURCE}
				getLabelValues={jobLabelValues}
				getSubjectContactId={jobSubjectContactId}
				renderMeta={renderJobMeta}
				renderExtraRows={renderJobExtraRows}
				resolveRequirementName={resolveRequirementName}
				resolveCompetenceName={resolveCompetenceName}
				emptyState={t("jobTimeline.noEvents")}
			/>
		</div>
	);
};

export default JobHistoryTab;
